/** venture/Step14Kit.tsx — 14단계 「투자확인서 발급」 키트 (2026-09-24 · 13단계 VICS 보고 삽입으로 13→14)
 *  회사별 카드: 발급 방식(복수·변경·이력) → 방식마다 필요한 정보·서류 · 신청 정보 요약 · 발급 완료. 조합 인수 CB 는 13단계 VICS 보고가 선행.
 *  한 회사가 조합 인수 전환사채 + 자본금 증자분처럼 방식을 여럿 고르면 묶음이 방식 수만큼 나온다(확인서도 그 수만큼). */
import React, { useState } from 'react';
import { mutStore, getUp, getUpKey, UpLabel } from './stepcommon';
import { s3upload } from './api';
import { openS3, downloadUploadFile } from './docgen';
import { kitKey } from './certplan';
import CoStatBar from './CoStatBar';
import CertPlanBox from './CertPlanBox';
import KitRow from './KitRow';
import { vicsDoneCount, vicsFundDone, VICS_CO_STAGES } from './vics13';
import KitPortal from './KitPortal';
import { useTmCompanies, useKitResults, useFocusTick } from './kithooks';
import { certPlanOf, regCoOf, fpLabel } from './coterms';
import { kitRows, kitSummary, invcertKey, invcertDone } from './kit13';
import { coStat } from './costep';
import { planModes, CERT_SHORT, CERT_MODES } from './types';
import type { Project, CoCard, CertMode } from './types';

interface Props { p: Project; onPatch: (patch: Partial<Project>) => void; toast: (m: string) => void; }

function colCls(cnt: number): string { return cnt >= 3 ? 'cols3' : (cnt === 2 ? 'cols2' : 'cols1'); }
const longLabel = (m: CertMode): string => (CERT_MODES.find((x) => x[0] === m) || ['', ''])[1];

function SummaryBox({ text, toast }: { text: string; toast: (m: string) => void }) {
  const [open, setOpen] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast('신청 정보를 복사했습니다'); }
    catch { toast('복사 실패 — 아래 내용을 직접 선택해 복사해 주세요'); setOpen(true); }
  };
  return (
    <div className="kitsum">
      <div className="codoc-h"><span>신청 정보 요약 <span className="s2tag">발급시스템에 옮겨 적을 값</span></span>
        <span style={{ display: 'flex', gap: 6 }}>
          <button className="rbtn" onClick={() => setOpen(!open)}>{open ? '접기' : '펼치기'}</button>
          <button className="rbtn ok" onClick={copy}>복사</button>
        </span>
      </div>
      {open ? <pre className="kitsum-pre">{text}</pre> : null}
    </div>
  );
}

/** 기타 첨부 — 정해진 항목 밖의 서류를 여러 개 (s13 up: <coId>_<mode>_etc_<n>) */
function EtcFiles({ p, co, mode, onPatch, toast }: { p: Project; co: CoCard; mode: CertMode; onPatch: (patch: Partial<Project>) => void; toast: (m: string) => void }) {
  const keys: string[] = [];
  for (let i = 1; i <= 20; i++) { const k = kitKey(co.id, mode, 'etc_' + i); if (getUp(p, 's13', k)) keys.push(k); }
  const nextKey = (): string => { for (let i = 1; i <= 20; i++) { const k = kitKey(co.id, mode, 'etc_' + i); if (!getUp(p, 's13', k)) return k; } return kitKey(co.id, mode, 'etc_20'); };
  const upload = async (f: File) => {
    try {
      const r = await s3upload(p.id, f); const k = nextKey();
      onPatch(mutStore(p, 's13', (st) => { st.up[k] = r.name; st.upKey = st.upKey || {}; st.upKey[k] = r.key; }));
      toast('첨부됨 · ' + r.name);
    } catch (e) { toast((e as Error).message); }
  };
  const remove = (k: string) => onPatch(mutStore(p, 's13', (st) => { if (st.up) delete st.up[k]; if (st.upKey) delete st.upKey[k]; }));
  return (
    <div className="codoc">
      <div className="codoc-h"><span>기타 첨부 <span className="s2tag">발급시스템이 추가로 요구하는 서류 · 여러 개 가능</span></span></div>
      {keys.map((k) => {
        const name = getUp(p, 's13', k); const s3key = getUpKey(p, 's13', k);
        return (
          <div className="s3file ok" key={k}><span className="rf">📄 {name}</span><span className="grow" />
            <button className="rbtn" onClick={() => openS3(s3key, name, true).catch((e) => toast((e as Error).message))}>👁</button>
            <button className="rbtn" onClick={() => downloadUploadFile(p, s3key, name, toast)}>⬇</button>
            <button className="rbtn danger" onClick={() => remove(k)}>🗑</button></div>
        );
      })}
      <UpLabel text="⬆ 기타 서류 추가 (또는 드래그)" onUp={upload} />
    </div>
  );
}

/** 방식 1개분 묶음 — 서류 · 요약 · 발급 완료 · 발급 결과 보관 */
function ModeKit({ p, co, mode, idx, total, onPatch, toast }: { p: Project; co: CoCard; mode: CertMode; idx: number; total: number; onPatch: (patch: Partial<Project>) => void; toast: (m: string) => void }) {
  const all = kitRows(p, co, mode);
  const rows = all.filter((r) => !r.item.tail);          // 신청에 필요한 것
  const tails = all.filter((r) => r.item.tail);          // 발급 후 보관 (발급본)
  const doneN = rows.filter((r) => r.done).length;
  const issued = invcertDone(p, co.id, mode, idx === 0);
  const toggleIssued = () => onPatch(mutStore(p, 's13', (s) => {
    s.chk = s.chk || {};
    const k = invcertKey(co.id, mode);
    const next = !issued;
    s.chk[k] = next;
    if (idx === 0) s.chk[co.id + '_invcert'] = next;   // 예전 단일 키도 같이 — 다른 화면(구 체크) 호환
  }));
  return (
    <div className={'modekit' + (issued ? ' done' : '')}>
      {total > 1 ? <div className="modekit-h"><span className="pill p-blue">{idx + 1}/{total}</span><b>{longLabel(mode)}</b><span className="s2tag">확인서 {idx + 1}장째</span></div> : null}
      {mode === 'cb_fund' ? (() => {
        // 조합 인수 CB 확인서는 VICS 투자실적 보고(13단계)가 먼저 — 여기서는 상태만 보여 준다
        const n = vicsDoneCount(p, co.id); const all = n === VICS_CO_STAGES.length; const sent = vicsFundDone(p);
        return (
          <div className={'s3file ' + (all && sent ? 'ok' : '')} style={{ marginTop: 8, flexWrap: 'wrap' }}>
            <span className="rf" style={{ whiteSpace: 'normal', flex: '1 1 200px' }}>🗂 VICS 투자실적 보고 (13단계) — 이 법인 {n}/{VICS_CO_STAGES.length} 화면 · 조합 확정/전송 {sent ? '완료' : '전'}</span>
            <span className={'pill ' + (all && sent ? 'p-green' : 'p-amber')}>{all && sent ? '선행 완료' : '먼저 마쳐야 발급 가능'}</span>
          </div>
        );
      })() : null}
      <div className="lbl" style={{ marginTop: 10 }}>필요 정보·서류 <span className="s2tag">{doneN}/{rows.length} 준비 · 앞단계·마스터·업체DB 자동</span></div>
      {rows.map((r) => <KitRow key={r.item.slot} p={p} co={co} mode={mode} row={r} onPatch={onPatch} toast={toast} />)}
      <EtcFiles p={p} co={co} mode={mode} onPatch={onPatch} toast={toast} />
      <KitPortal p={p} co={co} mode={mode} onPatch={onPatch} toast={toast} />
      <SummaryBox text={kitSummary(p, co, mode)} toast={toast} />
      <div className={'chkrow ' + (issued ? 'on' : '')} onClick={toggleIssued} style={{ marginTop: 10 }}>
        <span className="chkbox">{issued ? '✓' : ''}</span><b>투자확인서 발급 완료{total > 1 ? ' · ' + CERT_SHORT[mode] : ''}</b><span className="grow" />
        <span className="chklabel">{issued ? '발급 완료' : '발급 대기'}</span>
      </div>
      <div className={'kitresult' + (issued ? ' on' : '')}>
        <div className="lbl" style={{ marginTop: 0 }}>발급 결과 보관 <span className="s2tag">발급된 투자확인서 PDF — 조합원 소득공제 신청 때 씁니다</span></div>
        {tails.map((r) => <KitRow key={r.item.slot} p={p} co={co} mode={mode} row={r} onPatch={onPatch} toast={toast} />)}
      </div>
    </div>
  );
}

export default function Step14Kit({ p, onPatch, toast }: Props) {
  const cos: CoCard[] = (p.collect && p.collect.cos) || [];
  const [openCo, setOpenCo] = useState<Record<string, boolean>>({});
  // 열자마자 앞단계 밖의 값도 끌어온다 — 업체DB(법인등록번호·주소·설립일…)와 14단계 자료수집 포털 제출물
  const tick = useFocusTick();
  useTmCompanies(p, onPatch);
  useKitResults(p, onPatch, tick);
  const foldOf = (id: string): boolean => coStat(p, id, 14) === 'done' && !openCo[id];
  const toggleFold = (id: string) => setOpenCo((o) => ({ ...o, [id]: !o[id] }));

  if (!cos.length) return <div className="sact"><div className="sact-h">🧾 투자확인서 발급</div><div className="lbl" style={{ padding: '14px 0', textAlign: 'center' }}>등록된 투자법인이 없습니다</div></div>;

  return (
    <div className="sact">
      <div className="sact-h">🧾 투자확인서 발급</div>
      <div className="lbl">회사별 발급 방식(복수 가능)에 맞는 정보·서류를 모읍니다 · 앞단계(1·6·8·9·12단계)·마스터·업체DB 값은 자동으로 연동되고, 없으면 직접 채우거나 자료수집 링크로 받습니다</div>
      <div className={'cogrid ' + colCls(cos.length)}>
        {cos.map((co) => {
          const rc = regCoOf(p, co);
          const modes = planModes(certPlanOf(rc));
          const fp = fpLabel(rc);
          return (
            <div className={'cocol' + (foldOf(co.id) ? ' folded' : '')} key={co.id} id={'co-' + co.id + '-14'}>
              <div className="cocol-h">🏢 {co.name || '(법인)'}{fp ? <span className="s2tag" style={{ marginLeft: 8 }}>FP {fp}</span> : null}</div>
              <CoStatBar p={p} co={co} n={14} onPatch={onPatch} toast={toast} folded={foldOf(co.id)} onFold={() => toggleFold(co.id)} />
              {foldOf(co.id) ? null : (<>
                <CertPlanBox p={p} co={co} onPatch={onPatch} toast={toast} />
                {modes.length
                  ? modes.map((m, i) => <ModeKit key={m} p={p} co={co} mode={m} idx={i} total={modes.length} onPatch={onPatch} toast={toast} />)
                  : <div className="s3file" style={{ marginTop: 10 }}><span className="s3muted">발급 방식을 먼저 골라 주세요 — 방식에 따라 필요한 서류가 달라지고, 여러 개를 고르면 묶음이 그 수만큼 생깁니다</span></div>}
              </>)}
            </div>
          );
        })}
      </div>
    </div>
  );
}
